<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Layout from '../components/layout/Layout.vue'
import SeatMap from '../components/location/SeatMap.vue'
import StellarBackground from '../components/animations/StellarBackground.vue'
import CyberLoading from '../components/ui/CyberLoading.vue'
import deLoods from '../assets/backgrounds/deLoods.jpg'
import parkingLoods from '../assets/backgrounds/parkingLoods.jpg'

const { t, tm } = useI18n()
const isLoading = ref(true)

onMounted(() => {
  setTimeout(() => {
    isLoading.value = false
  }, 1000)
})

const facilitiesList = computed(() => {
  const facilities = tm('location.info.facilityList');
  return Array.isArray(facilities) ? facilities : []; 
})

const address = "Douaneplein 1, 2800 Mechelen, Belgium"
const navigationLinks = computed(() => ({
  google: `https://www.google.com/maps/dir//De+Loods,+Douaneplein,+2800+Mechelen/@51.0262022,4.4918772,18z/data=!4m17!1m7!3m6!1s0x47c3e434019741e7:0x17c594a2a5faa1ff!2sDe+Loods!8m2!3d51.0261143!4d4.4931812!16s%2Fg%2F11b7ljf1d8!4m8!1m0!1m5!1m1!1s0x47c3e434019741e7:0x17c594a2a5faa1ff!2m2!1d4.4931812!2d51.0261143!3e1?entry=ttu&g_ep=EgoyMDI1MDExMC4wIKXMDSoASAFQAw%3D%3D`,
  waze: `https://www.waze.com/nl/live-map/directions/de-loods-douaneplein-mechelen?navigate=yes&to=place.w.2949630.29430767.4214865`,
  apple: `https://beta.maps.apple.com/?address=Douaneplein+4%2C+2800+Mechelen%2C+Belgi%C3%AB&auid=2347247336675405679&ll=51.0254702%2C4.4932233&lsp=9902&q=De+Loods`
}))

</script>

<template>
  <Layout>
    <div class="location-wrapper">
      <StellarBackground backColor="#2d3434" :circle-offset="{x: 350, y: 100}"/>
      <div class="location-container">
        <CyberLoading :is-loading="isLoading" />
        <div class="location-page" :class="{ 'content-loaded': !isLoading }">
          <!-- Header Section -->
          <div class="location-header">
            <h1>{{ t('location.title') }}</h1>
            <p>{{ t('location.subtitle') }}</p>
          </div>

          <!-- Main Content -->
          <div class="content-section">
            <v-row>
              <!-- Location Info -->
              <v-col cols="12" md="6">
                <div class="section-container">
                  <h2 class="section-title">
                    <v-icon class="section-icon" color="#76c4ae">mdi-information</v-icon>
                    {{ t('location.info.title') }}
                  </h2>
                  <v-card class="content-card" elevation="0">
                    <v-card-text>
                      <v-row class="d-flex align-stretch">
                        <v-col cols="12" md="6">
                            <v-row>
                              <v-col cols="auto" class="mr-4">
                                <h3 class="text-h6 mb-2">{{ t('location.info.address') }}</h3>
                                <p class="text-body-1 mt-2 gap-2">
                                  De Loods<br>
                                  Douaneplein 1<br>
                                  2800 Mechelen<br>
                                  Belgium
                                </p>
                              </v-col>
                              <v-col cols="8">
                                <div class="navigation-buttons">
                                  <v-btn
                                    icon
                                    variant="flat"
                                    color="#76c4ae"
                                    class="mr-2"
                                    :href="navigationLinks.google"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <v-icon>mdi-google-maps</v-icon>
                                  </v-btn>
                                  <v-btn
                                    icon
                                    variant="flat"
                                    color="#76c4ae"
                                    class="mr-2"
                                    :href="navigationLinks.waze"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <v-icon>mdi-waze</v-icon>
                                  </v-btn>
                                  <v-btn
                                    icon
                                    variant="flat"
                                    color="#76c4ae"
                                    :href="navigationLinks.apple"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <v-icon>mdi-apple-ios</v-icon>
                                  </v-btn>
                                </div>
                              </v-col>
                            </v-row>
                        </v-col>
                        <v-col cols="12" md="6">
                          <div>
                            <h3 class="text-h6 mb-2">{{ t('location.info.facilities') }}</h3>
                            <div class="facilities-list">
                              <div v-for="facility, index in facilitiesList" :key="index" class="facility-item">
                                <v-icon color="#df824d" class="mr-2">mdi-check</v-icon>
                                <span class="facility-text">{{ facility }}</span>
                              </div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                      <v-img
                        :src="deLoods"
                        height="400"
                        cover
                        class="rounded-lg venue-image mt-4"
                      />
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>

              <!-- Parking Info -->
              <v-col cols="12" md="6">
                <div class="section-container">
                  <h2 class="section-title">
                    <v-icon class="section-icon" color="#76c4ae">mdi-parking</v-icon>
                    {{ t('location.parking.title') }}
                  </h2>
                  <v-card class="content-card" elevation="0">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="6">
                          <div class="mb-4">
                            <h3 class="text-h6 mb-2">{{ t('location.parking.options') }}</h3>
                            <div class="parking-list">
                              <div v-for="(option, index) in tm('location.parking.parkingList')" :key="index" class="parking-item">
                                <v-icon color="#df824d" class="mr-2">mdi-check</v-icon>
                                <span>{{ option }}</span>
                              </div>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="12" md="6">
                          <div>
                            <h3 class="text-h6 mb-2">{{ t('location.parking.options') }}</h3>
                            <div class="parking-list">
                              <div v-for="(option, index) in tm('location.parking.parkingOptions')" :key="index" class="parking-item">
                                <v-icon color="#df824d" class="mr-2">mdi-check</v-icon>
                                <span>{{ option }}</span>
                              </div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                      <v-img
                        :src="parkingLoods"
                        height="400"
                        cover
                        class="rounded-lg parking-image mt-4"
                      />
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>
            </v-row>

            <!-- Seat Map -->
            <div class="section-container">
              <h2 class="section-title">
                <v-icon class="section-icon" color="#76c4ae">mdi-floor-plan</v-icon>
                {{ t('location.seatmap.title') }}
              </h2>
              <div class="section-container-map">
                <SeatMap />
              </div>
            </div>
            <div class="bottom-links d-none d-md-flex">
              <a href="https://alterlan.be/privacy" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.privacy')"></a>
            <a href="https://alterlan.be/terms" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.terms')"></a>
            <router-link to="/contact" class="nav-link" v-html="t('menu.footerLinks.contact')"></router-link>
          </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>

.location-wrapper {
  position: relative;
  min-height: calc(100vh - 80px);
  width: 100%;
  overflow-y: hidden;
  scroll-behavior: smooth;
}

:deep(.stellar-background) {
  position: fixed;
  top: 80px;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.location-container {
  min-width: 100vw !important;
  z-index: 1;
  overflow-x: hidden;
}

.location-page {
  position: relative;
  padding: 2rem;
  min-height: calc(100vh - 80px);
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;
}

.content-loaded {
  opacity: 1;
  transform: translateY(0);
}

.bottom-links {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4rem;
  padding: 1rem 0 1rem 0;
}

.nav-link {
  color: white;
  min-width: 100px;
  text-align: center;
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #e69b71;
}

@media (max-width: 768px) {
  .bottom-links {
    gap: 2rem;
  }

  .nav-link {
    font-size: 0.7rem;
  }
}

.location-header {
  text-align: center;
  position: relative;
  margin-bottom: 3rem;
  margin-top: 3rem;
}

.location-header h1 {
  font-family: 'Airborne GP', sans-serif;
  font-size: 3.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #76c4ae;
  letter-spacing: 4px;
  position: relative;
  display: inline-block;
}

.location-header p {
  font-size: 1.2rem;
  color: #ccc;
  margin: 1rem auto 0;
  line-height: 1.6;
  position: relative;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.content-section {
  width: 100%;
  max-width: 100vw;
  margin: 0 auto;
  padding: 2rem;
}

.section-container {
  margin-bottom: 4rem;
  width: 100%;
}

.section-container-map {
  margin-bottom: 2rem;
  width: 100%;
  top: 0 !important;
  position: relative;
}

@media (max-width: 960px) {
  .section-container-map {
    top: 0 !important;
  }

  .location-wrapper {
    min-height: calc(100vh - 80px);
  }


}

.content-card {
  background: rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  box-shadow: #76c4ae 0 0, #76c4ae80 0 0;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  max-width: 100%;
  margin: 1rem 0;
  align-self: stretch;
  min-height: 720px;
  position: relative;
}

@media (max-width: 960px) {
  .content-card {
    min-height: 780px;
  }
}

@media (max-width: 525px) {
  .content-card {
    min-height: 840px;
  }
}

@media (max-width: 451px) {
  .content-card {
    min-height: 880px;
  }
}

@media (max-width: 338px) {
  .content-card {
    min-height: 900px;
  }
}

@media (max-width: 1040px) {
  
  .location-page {
    padding: 1.5rem;
  }

  .content-section {
    padding: 0 !important;
  }
}



.content-card:hover {
    box-shadow: rgba(118, 196, 174, 0.3) -10px 10px, rgba(118, 196, 174, 0.2) -20px 20px !important;
}

@media (max-width: 960px) {
  .content-section {
    padding: 1rem;
  }
  
  .content-card {
    margin: 0.5rem 0;
    width: 100% !important;
  }

  .section-container {
    margin-bottom: 2rem;
  }

  .facilities-list,
  .parking-list {
    flex-direction: column;
    width: 100%;
  }
}

@media (max-width: 768px) {
  .location-header {
    padding: 1rem;
  }

  .location-header h1 {
    font-size: 2rem;
    text-align: center;
  }
  
  .location-header p {
    width: 100%;
    text-align: center;
  }
}

.facilities-list,
.parking-list {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.section-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: white;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.facility-item,
.parking-item {
  display: flex;
  align-items: center;
  color: #FFF;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.inline-item {
  display: inline-flex;
  align-items: center;
  margin-right: 1.5rem;
  margin-bottom: 0.5rem;
}

.venue-image,
.parking-image {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  position: absolute;
  bottom: 10px;
  left: 1%;
  z-index: 10;
  width: 98%;
}

.navigation-buttons {
  display: flex;
  flex-direction: row;
  align-items: start; 
  justify-content: start;
  gap: 0.5rem;
  max-width: 50px;
}

html {
  scroll-behavior: smooth;
}
</style>
